import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const workspaceKey = (requestId) => ['sales', 'workspace', requestId];

// GET /sales/requests/:requestId — { request, quotation } where quotation is
// null until the first save (services/quotationService.js#getWorkspace).
export function useWorkspace(requestId) {
  return useQuery({
    queryKey: workspaceKey(requestId),
    queryFn: () => apiClient.get(`/sales/requests/${requestId}`),
    enabled: Boolean(requestId),
  });
}

// PUT /sales/requests/:requestId/lines — full replace of the draft's line
// set (services/quotationService.js#saveLinePrices), plus (editable-terms
// task) the three document-terms fields saved alongside it. `body` is
// `{ lines, validUntil, deliveryDays, paymentTerms }` — the three terms are
// optional/nullable and left untouched server-side when omitted (see
// quotationRepository.updateHeaderTerms's COALESCE behavior), so a caller
// that only wants to save lines can still pass just `{ lines }`. On
// success, MERGES the response into both this workspace's cache and the
// queue list cache, rather than replacing wholesale — the save response
// shape is `{ request, quotation }`, missing the `defaults` key
// getWorkspace's own GET response carries, so a plain replace would drop
// the settings-derived prefill defaults from the cache after the first
// save.
export function useSaveLines(requestId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiClient.put(`/sales/requests/${requestId}/lines`, body),
    onSuccess: (data) => {
      queryClient.setQueryData(workspaceKey(requestId), (prev) =>
        prev ? { ...prev, ...data } : data
      );
      queryClient.invalidateQueries({ queryKey: ['sales', 'queue'] });
    },
  });
}

// POST /sales/requests/:requestId/submit — moves the quotation to
// pending_approval (services/quotationService.js#submitForApproval). payload
// is optional and, since migration 020 (sealed-bid tenders), may carry
// `{ sealed, bidOpeningAt }` — omitting it entirely still submits unsealed,
// unchanged from before that migration.
export function useSubmitQuotation(requestId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => apiClient.post(`/sales/requests/${requestId}/submit`, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(workspaceKey(requestId), (prev) =>
        prev ? { ...prev, quotation: data.quotation } : prev
      );
      queryClient.invalidateQueries({ queryKey: ['sales', 'queue'] });
    },
  });
}

// POST /sales/requests/:requestId/unseal — irreversible (services/
// quotationService.js#unsealLatestForRequest): no reseal path exists.
// Available to any salesperson assigned to this request. Same cache-update
// shape as useSubmitQuotation above.
export function useUnsealQuotation(requestId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.post(`/sales/requests/${requestId}/unseal`),
    onSuccess: (data) => {
      queryClient.setQueryData(workspaceKey(requestId), (prev) =>
        prev ? { ...prev, quotation: data.quotation } : prev
      );
    },
  });
}

export default useWorkspace;
